import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Phone, Mail, MapPin, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Name, phone & message are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success("Message saved! Opening WhatsApp…");
      setForm({ name: "", phone: "", email: "", message: "" });
      window.open(data.whatsapp_url, "_blank");
    } catch (err) {
      toast.error("Could not send. Try calling 9235372759.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="py-12 md:py-16" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs tracking-[0.2em] uppercase text-holiday-red font-bold">Contact</p>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] mt-3">Say <span className="text-holiday-red italic font-serif">hi</span>. Or, hit our <br className="hidden md:block"/>WhatsApp directly.</h1>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-7 bg-white rounded-[2rem] p-7 md:p-10 cushion border border-[#F0E4E4] space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-choc/70">Your name *</label>
                <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="contact-name-input" className="mt-1 w-full rounded-2xl border border-choc/15 px-4 py-3 focus:outline-none focus:border-holiday-red bg-cream"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-choc/70">Phone *</label>
                <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required data-testid="contact-phone-input" className="mt-1 w-full rounded-2xl border border-choc/15 px-4 py-3 focus:outline-none focus:border-holiday-red bg-cream"/>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-choc/70">Email (optional)</label>
              <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="contact-email-input" className="mt-1 w-full rounded-2xl border border-choc/15 px-4 py-3 focus:outline-none focus:border-holiday-red bg-cream"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-choc/70">Message *</label>
              <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required rows={5} data-testid="contact-message-input" className="mt-1 w-full rounded-2xl border border-choc/15 px-4 py-3 focus:outline-none focus:border-holiday-red bg-cream resize-none" placeholder="Tell us what you'd like — flavour question, custom order, feedback…"/>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <button type="submit" disabled={busy} data-testid="contact-submit-btn" className="inline-flex items-center gap-2 bg-holiday-red text-white px-7 py-3.5 rounded-full font-bold hover:bg-holiday-red-deep disabled:opacity-50 transition-colors">
                <Send className="w-4 h-4"/> {busy ? "Sending…" : "Send & open WhatsApp"}
              </button>
              <a href="tel:9235372759" className="inline-flex items-center gap-2 bg-choc text-cream px-7 py-3.5 rounded-full font-bold">
                <Phone className="w-4 h-4"/> Call instead
              </a>
            </div>
          </form>

          {/* Info column */}
          <aside className="lg:col-span-5 space-y-4">
            <div className="bg-ice-pink rounded-[2rem] p-7 cushion border border-[#F0E4E4]">
              <h3 className="font-display text-2xl">Visit the parlour</h3>
              <p className="text-sm leading-relaxed text-choc/80 mt-2 flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0"/> Road No.12, G‑23, near Springer School, behind Ramada Hotel, Lachhipur Industrial Area, Gorakhpur, UP 273015</p>
              <a href="https://maps.app.goo.gl/xd1he1c7UpNeaTuV9" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 underline font-semibold text-sm">Google Maps directions →</a>
            </div>
            <div className="bg-mint rounded-[2rem] p-7 cushion border border-[#F0E4E4]">
              <h3 className="font-display text-2xl">Phone & Email</h3>
              <ul className="mt-3 space-y-1.5 text-sm">
                <li><a className="flex items-center gap-2 hover:text-holiday-red" href="tel:9235372759"><Phone className="w-3.5 h-3.5"/> 9235372759</a></li>
              </ul>
              <a className="mt-2 inline-flex items-center gap-2 hover:text-holiday-red" href="mailto:harshicepartner@gmail.com"><Mail className="w-3.5 h-3.5"/> harshicepartner@gmail.com</a>
              <a href="https://wa.me/919235372759" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 bg-choc text-cream px-4 py-2 rounded-full font-bold text-sm">
                <MessageCircle className="w-4 h-4"/> WhatsApp us
              </a>
            </div>
            <div className="bg-sun-yellow-soft rounded-[2rem] p-7 cushion border border-[#F0E4E4]">
              <h3 className="font-display text-2xl">Open hours</h3>
              <p className="font-serif italic text-lg mt-2">Open 24 hours · 7 days</p>
              <p className="text-sm text-choc/70 mt-2">Late-night kulfi crisis? We're awake.</p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}



